import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import {
  type AnchorRestoreResult,
  type DailyLogSearchNavigationEntry,
  type ScrollAnchor,
  captureScrollAnchor,
  focusRowLink,
  restoreScrollAnchor,
} from "./dailyLogSearchNavigation";
import type { DailyLogSearchStatus } from "./useDailyLogSearch";

export type RestoreSettledResult =
  | { restored: true; cacheHit: boolean; result: Extract<AnchorRestoreResult, { restored: true }> }
  | { restored: false; cacheHit: boolean; reason: "no_rows" | "fetch_failed" };

interface UseScrollRestorationParams {
  containerRef: RefObject<HTMLElement>;
  /** 이 목록 history entry에 저장된 복귀 위치. 복귀가 아니면 null */
  entry: DailyLogSearchNavigationEntry | null;
  status: DailyLogSearchStatus;
  /** 렌더된 행이 바뀔 때마다 보류된 anchor를 적용한다 */
  items: readonly unknown[];
  /** 적용된 검색 조건. 복원 전에 바뀌면 복원을 포기한다(새 조건은 맨 위에서 시작) */
  conditionsKey: string;
  onSettled: (result: RestoreSettledResult) => void;
}

/**
 * 검색 목록 스크롤 컨테이너의 위치를 이 모듈이 소유한다 (Router 자동 복원은 쓰지 않는다).
 * - 상세 복귀: 첫 렌더 후 저장된 anchor+offset으로 맞추고 선택했던 링크로 초점을 돌린다.
 * - 목록 교체(갱신): 교체 직전 화면 상단 anchor를 잡아 교체 후 같은 위치로 보정한다.
 */
export function useDailyLogSearchScrollRestoration({
  containerRef,
  entry,
  status,
  items,
  conditionsKey,
  onSettled,
}: UseScrollRestorationParams) {
  const isInitialRestorePendingRef = useRef(entry !== null);
  const cacheHitRef = useRef<boolean | null>(null);
  const pendingAnchorRef = useRef<ScrollAnchor | null>(null);
  /** 목록 교체 횟수. 새 데이터가 이전과 같으면 items가 그대로라 이 값으로 보류 anchor 적용을 깨운다 */
  const [chainSwapCount, setChainSwapCount] = useState(0);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  if (cacheHitRef.current === null && status !== "idle") {
    cacheHitRef.current = status === "success";
  }

  const initialConditionsKeyRef = useRef(conditionsKey);
  useEffect(
    function abandonRestoreOnConditionChange() {
      if (initialConditionsKeyRef.current !== conditionsKey) isInitialRestorePendingRef.current = false;
    },
    [conditionsKey],
  );

  // 목록이 교체된 직후 렌더에서 보류된 anchor를 적용해야 하므로 items와 교체 횟수를 의존성에 둔다.
  // biome-ignore lint/correctness/useExhaustiveDependencies: 렌더된 행·목록 교체가 실행 조건
  useLayoutEffect(
    function applyScrollPosition() {
      const container = containerRef.current;
      if (!container || status !== "success") return;

      if (isInitialRestorePendingRef.current && entry) {
        isInitialRestorePendingRef.current = false;
        // 캐시 없는 복원은 행이 없는 스켈레톤 위에서 anchor를 잡는다. 복귀 위치가 우선이므로 버린다.
        pendingAnchorRef.current = null;
        const cacheHit = cacheHitRef.current ?? false;
        const result = restoreScrollAnchor(container, entry);
        if (!result.restored) {
          onSettledRef.current({ restored: false, cacheHit, reason: "no_rows" });
          return;
        }
        const hasSelectedRow = container.querySelector(`[data-daily-log-id="${entry.selectedLogId}"]`) !== null;
        focusRowLink(container, hasSelectedRow ? entry.selectedLogId : result.logId);
        onSettledRef.current({ restored: true, cacheHit, result });
        return;
      }

      const pendingAnchor = pendingAnchorRef.current;
      if (pendingAnchor) {
        pendingAnchorRef.current = null;
        restoreScrollAnchor(container, pendingAnchor);
      }
    },
    [items, chainSwapCount, status, entry, containerRef],
  );

  /** 캐시 없는 복원을 포기하고 처음부터 보기로 했을 때 */
  const abandonInitialRestore = useCallback(() => {
    if (!isInitialRestorePendingRef.current) return;
    isInitialRestorePendingRef.current = false;
    onSettledRef.current({ restored: false, cacheHit: cacheHitRef.current ?? false, reason: "fetch_failed" });
  }, []);

  /** coordinator가 목록을 교체하기 직전에 호출한다 */
  const preserveAnchorForNextRender = useCallback(() => {
    const container = containerRef.current;
    if (container) pendingAnchorRef.current = captureScrollAnchor(container);
  }, [containerRef]);

  /** coordinator가 목록을 교체한 직후에 호출한다 */
  const applyAnchorAfterSwap = useCallback(() => setChainSwapCount((count) => count + 1), []);

  return { preserveAnchorForNextRender, applyAnchorAfterSwap, abandonInitialRestore };
}
